/**
 * SegmentedControl: 2–5 short options that apply at once (Theme, page size preview, list/grid) (cockatrice-ui
 * `src/ui/SegmentedControl.jsx`, ported to hyperscript; PLAN.md §0).
 * A radiogroup of buttons with a roving tabindex: Tab enters on the chosen option,
 * arrow keys move and choose, Home/End jump to the ends.
 * options: [{ value, label, icon, disabled }]. Icon-only options need a label too (used as aria-label).
 * onChange(value). Name it with label (aria-label) or aria-labelledby.
 */
import './css/segmented-control.css';
import { createSignal, mergeProps, splitProps } from 'solid-js';
import { useField } from './Field.ts';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, each, el, spread, when, whenSome } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

/** One segment. */
export interface SegmentedOption {
  /** Value reported by onChange. */
  readonly value: string;
  /** Text; also the name when iconOnly. */
  readonly label: string;
  /** Optional icon. */
  readonly icon?: IconName | undefined;
  /** Not selectable. */
  readonly disabled?: boolean | undefined;
}

export interface SegmentedControlProps extends Attributes {
  /** Names the group (aria-label). */
  readonly label?: string | undefined;
  /** 2–5 short options. */
  readonly options: readonly SegmentedOption[];
  /** Controlled value. */
  readonly value?: string | undefined;
  /** Uncontrolled starting value. */
  readonly defaultValue?: string | undefined;
  /** Called when the choice changes. */
  readonly onChange?: ((value: string) => void) | undefined;
  /** Segment height. */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** Share the full width equally. */
  readonly fullWidth?: boolean | undefined;
  /** Show icons only; labels become names and tooltips. */
  readonly iconOnly?: boolean | undefined;
  /** Disables the control. */
  readonly disabled?: boolean | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
}

export default function SegmentedControl(props: SegmentedControlProps): Child {
  const p = mergeProps({ size: 'md' } as const, props);
  const [local, rest] = splitProps(p, ['label', 'options', 'value', 'defaultValue', 'onChange', 'size', 'fullWidth', 'iconOnly', 'disabled', 'class']);
  const field = useField();
  const [inner, setInner] = createSignal(local.defaultValue);
  const current = (): string | undefined => local.value ?? inner();
  const enabled = (): SegmentedOption[] => local.options.filter((o) => o.disabled !== true && local.disabled !== true);
  const tabStop = (): string | undefined => {
    const on = enabled().find((o) => o.value === current());
    return (on ?? enabled()[0])?.value;
  };
  let root: HTMLDivElement | undefined;

  const choose = (o: SegmentedOption): void => {
    if (o.disabled === true || local.disabled === true) return;
    setInner(o.value);
    local.onChange?.(o.value);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    const list = enabled();
    const i = list.findIndex((o) => o.value === current());
    let j: number | null = null;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') j = (i + 1) % list.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') j = (i - 1 + list.length) % list.length;
    else if (event.key === 'Home') j = 0;
    else if (event.key === 'End') j = list.length - 1;
    if (j === null || !list.length) return;
    // `j` is a place in the non-empty `list` (`i` is -1 at least), so this runs once, with `list[j]`.
    for (const next of list.slice(j, j + 1)) {
      event.preventDefault();
      choose(next);
      root?.querySelector<HTMLElement>(`[data-value="${CSS.escape(next.value)}"]`)?.focus();
    }
  };

  const cls = (): string =>
    ['ui-segmented', `ui-segmented--${local.size}`, local.fullWidth === true && 'ui-segmented--full', local.disabled === true && 'is-disabled', local.class]
      .filter(Boolean)
      .join(' ');

  return el(
    'div',
    spread(rest, {
      ref: (node: HTMLDivElement) => {
        root = node;
      },
      class: cls,
      role: 'radiogroup',
      'aria-label': () => local.label,
      'aria-labelledby': () => (!local.label ? (rest['aria-labelledby'] ?? field?.labelId) : undefined),
      'aria-disabled': () => (local.disabled === true ? 'true' : undefined),
      onKeyDown,
    }),
    each(
      () => local.options,
      (o) => {
        const on = (): boolean => current() === o.value;
        return el(
          'button',
          {
            type: 'button',
            role: 'radio',
            class: 'ui-segmented__option',
            'data-value': () => o.value,
            'aria-checked': () => (on() ? 'true' : 'false'),
            'aria-label': () => (local.iconOnly === true ? o.label : undefined),
            title: () => (local.iconOnly === true ? o.label : undefined),
            tabindex: () => (tabStop() === o.value ? 0 : -1),
            disabled: () => o.disabled === true || local.disabled === true,
            onClick: () => {
              choose(o);
            },
          },
          whenSome(
            () => o.icon,
            (icon) => component(Icon, { name: icon(), size: 'sm' }),
          ),
          when(
            () => local.iconOnly !== true,
            () => el('span', {}, () => o.label),
          ),
        );
      },
    ),
  );
}
